import React, { useState, useEffect, useRef } from 'react';
import {
    ShieldAlert,
    ShieldCheck,
    Mail,
    Lock,
    KeyRound,
    Copy,
    Check,
    ArrowRight,
    Loader2,
    LogOut,
    RefreshCw,
    Sparkles,
    Key,
    Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export const MandatoryTwoFactorLock: React.FC = () => {
    const { user, logout, toggleTwoFactor, refreshUser } = useAuth();
    const [activeTab, setActiveTab] = useState<'2fa' | 'password'>('2fa');
    const [isCheckingExempt, setIsCheckingExempt] = useState(false);
    
    // 2FA Setup State
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [timer, setTimer] = useState<number>(0);
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [copied, setCopied] = useState(false);

    // Password Change State
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    const token = localStorage.getItem('token');

    // Automatically check for instructor/admin exemption on mount
    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const handleCheckExemption = async () => {
        setIsCheckingExempt(true);
        try {
            const fresh = await refreshUser();
            if (fresh?.two_factor_exempt) {
                toast.success('Special 2FA exemption detected! Unlocking dashboard...');
            } else if (fresh?.two_factor_enabled) {
                toast.success('2FA is active. Unlocking dashboard...');
            } else {
                toast.error('No active 2FA exemption found. Please complete 2FA setup or contact your instructor.');
            }
        } catch {
            toast.error('Could not verify status. Please try again.');
        } finally {
            setIsCheckingExempt(false);
        }
    };

    useEffect(() => {
        let interval: any;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const handleSendCode = async () => {
        setIsSendingCode(true);
        try {
            const res = await fetch(`${API_URL}/2fa/send-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ email: user?.email })
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.message || `Verification code sent to ${user?.email}`);
                setTimer(60);
                setStep(2);
                setTimeout(() => inputRefs.current[0]?.focus(), 150);
            } else {
                toast.error(data.message || 'Failed to send verification code');
            }
        } catch (err) {
            toast.error('Network error. Unable to send verification code.');
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) {
            const pasted = value.replace(/\D/g, '').slice(0, 6).split('');
            const pastedOtp = [...otp];
            pasted.forEach((char, i) => {
                if (i < 6) pastedOtp[i] = char;
            });
            setOtp(pastedOtp);
            const nextFocus = Math.min(pasted.length, 5);
            inputRefs.current[nextFocus]?.focus();
            return;
        }

        const cleanVal = value.replace(/\D/g, '');
        const newOtp = [...otp];
        newOtp[index] = cleanVal;
        setOtp(newOtp);

        if (cleanVal && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        const fullCode = otp.join('');
        if (fullCode.length !== 6) {
            toast.error('Please enter the full 6-digit code');
            return;
        }

        setIsVerifying(true);
        try {
            const res = await fetch(`${API_URL}/2fa/enable`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ code: fullCode })
            });

            const data = await res.json();
            if (res.ok) {
                if (Array.isArray(data.recovery_codes)) {
                    setRecoveryCodes(data.recovery_codes);
                }
                setStep(3);
                toast.success('Two-Factor Authentication successfully enabled!');
            } else {
                toast.error(data.message || 'Invalid verification code');
            }
        } catch (err) {
            toast.error('Network error while verifying code.');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleCopyCodes = () => {
        if (recoveryCodes.length > 0) {
            navigator.clipboard.writeText(recoveryCodes.join('\n'));
            setCopied(true);
            toast.success('Recovery codes copied to clipboard');
            setTimeout(() => setCopied(false), 3000);
        }
    };

    const handleCompleteSetup = () => {
        // Unlock access by updating 2FA in context and local storage
        toggleTwoFactor(true);
        toast.success('Account security configured. Welcome to your dashboard!');
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword.length < 8) {
            toast.error('New password must be at least 8 characters long');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        setIsChangingPassword(true);
        try {
            const res = await fetch(`${API_URL}/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                    new_password_confirmation: confirmPassword
                })
            });

            const data = await res.json();
            if (res.ok) {
                toast.success('Password updated successfully!');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setActiveTab('2fa');
            } else {
                toast.error(data.message || 'Failed to update password');
            }
        } catch (err) {
            toast.error('Network error while changing password.');
        } finally {
            setIsChangingPassword(false);
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 99999,
                background: 'linear-gradient(135deg, #091e14 0%, #064e3b 50%, #042f2e 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1.5rem',
                overflowY: 'auto',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}
        >
            <div
                style={{
                    background: '#ffffff',
                    width: '100%',
                    maxWidth: '560px',
                    borderRadius: '24px',
                    padding: '2.5rem',
                    boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.4)',
                    color: '#1e293b',
                    position: 'relative'
                }}
            >
                {/* Header Badge & Actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fef2f2', border: '1px solid #fecaca', padding: '6px 14px', borderRadius: '50px', color: '#dc2626', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        <Lock size={14} /> Security Action Required
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                            type="button"
                            onClick={handleCheckExemption}
                            disabled={isCheckingExempt}
                            title="Check if instructor granted you a 2FA exemption"
                            style={{
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                color: '#065f46',
                                padding: '6px 12px',
                                borderRadius: '10px',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                cursor: isCheckingExempt ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <RefreshCw size={13} className={isCheckingExempt ? 'animate-spin' : ''} />
                            {isCheckingExempt ? 'Checking...' : 'Check Exemption'}
                        </button>

                        <button
                            type="button"
                            onClick={logout}
                            title="Sign Out"
                            style={{
                                background: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                color: '#64748b',
                                padding: '6px 12px',
                                borderRadius: '10px',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <LogOut size={13} /> Sign Out
                        </button>
                    </div>
                </div>

                {/* Sub-Navigation Tabs */}
                <div style={{ display: 'flex', background: '#f8fafc', padding: '4px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '1.5rem', gap: '4px' }}>
                    <button
                        type="button"
                        onClick={() => setActiveTab('2fa')}
                        style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '10px',
                            border: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 800,
                            cursor: 'pointer',
                            background: activeTab === '2fa' ? '#0F5132' : 'transparent',
                            color: activeTab === '2fa' ? '#ffffff' : '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Shield size={15} /> 1. Set Up 2FA {recoveryCodes.length > 0 ? '✓' : ''}
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('password')}
                        style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '10px',
                            border: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 800,
                            cursor: 'pointer',
                            background: activeTab === 'password' ? '#0F5132' : 'transparent',
                            color: activeTab === 'password' ? '#ffffff' : '#64748b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Key size={15} /> 2. Update Password
                    </button>
                </div>

                {activeTab === '2fa' ? (
                    <>
                        {/* Title & Description */}
                        <div style={{ marginBottom: '1.75rem' }}>
                            <h2 style={{ fontSize: '1.55rem', fontWeight: 900, color: '#0f172a', margin: '0 0 0.5rem 0', lineHeight: 1.25 }}>
                                {step === 3 ? 'Two-Step Verification Enabled!' : 'Enable 2FA to Unlock Your Account'}
                            </h2>
                            <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0, lineHeight: 1.55 }}>
                                {step === 3
                                    ? 'Your account is now fully protected. Save your single-use recovery codes in case you ever lose access to your email.'
                                    : 'To protect LMS coursework, live classes, and student records, Two-Factor Authentication (2FA) is mandatory for all accounts.'}
                            </p>
                        </div>

                        {/* STEP 1: INITIAL PROMPT */}
                        {step === 1 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Mail size={22} />
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            Registered Email Address
                                        </div>
                                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', wordBreak: 'break-all', marginTop: '2px' }}>
                                            {user?.email}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '14px', padding: '1rem', fontSize: '0.85rem', color: '#166534', lineHeight: 1.5 }}>
                                    💡 We will send a 6-digit verification code to your email to confirm and activate two-step authentication on your account.
                                </div>

                                <button
                                    type="button"
                                    onClick={handleSendCode}
                                    disabled={isSendingCode}
                                    style={{
                                        width: '100%',
                                        height: '52px',
                                        background: '#0F5132',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '14px',
                                        fontSize: '1rem',
                                        fontWeight: 800,
                                        cursor: isSendingCode ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        boxShadow: '0 10px 20px rgba(15, 81, 50, 0.25)',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {isSendingCode ? <Loader2 size={20} className="animate-spin" /> : <>Send Verification Code <ArrowRight size={18} /></>}
                                </button>
                            </div>
                        )}

                        {/* STEP 2: ENTER OTP */}
                        {step === 2 && (
                            <form onSubmit={handleVerifyCode} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                        Enter the 6-digit code sent to <strong style={{ color: '#0f172a' }}>{user?.email}</strong>
                                    </span>
                                </div>

                                {/* 6 Digit Input Grid */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', maxWidth: '380px', margin: '0 auto', width: '100%' }}>
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={(el) => { inputRefs.current[index] = el; }}
                                            type="text"
                                            maxLength={1}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(index, e)}
                                            style={{
                                                width: '100%',
                                                height: '54px',
                                                fontSize: '1.5rem',
                                                fontWeight: 800,
                                                textAlign: 'center',
                                                borderRadius: '12px',
                                                border: digit ? '2px solid #0F5132' : '2px solid #e2e8f0',
                                                background: digit ? '#f0fdf4' : '#f8fafc',
                                                color: '#0f172a',
                                                outline: 'none',
                                                transition: 'all 0.2s'
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Resend Timer */}
                                <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#64748b' }}>
                                    {timer > 0 ? (
                                        <span>Resend code in <strong>{timer}s</strong></span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleSendCode}
                                            disabled={isSendingCode}
                                            style={{ background: 'none', border: 'none', color: '#0F5132', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        >
                                            <RefreshCw size={13} /> Resend verification code
                                        </button>
                                    )}
                                </div>

                                {/* Verify Button */}
                                <button
                                    type="submit"
                                    disabled={isVerifying || otp.join('').length !== 6}
                                    style={{
                                        width: '100%',
                                        height: '52px',
                                        background: '#0F5132',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '14px',
                                        fontSize: '1rem',
                                        fontWeight: 800,
                                        cursor: isVerifying || otp.join('').length !== 6 ? 'not-allowed' : 'pointer',
                                        opacity: otp.join('').length === 6 ? 1 : 0.6,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        boxShadow: '0 10px 20px rgba(15, 81, 50, 0.25)',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {isVerifying ? <Loader2 size={20} className="animate-spin" /> : <>Verify & Enable 2FA <ShieldCheck size={18} /></>}
                                </button>
                            </form>
                        )}

                        {/* STEP 3: RECOVERY CODES & UNLOCK */}
                        {step === 3 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '1.25rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                        <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <KeyRound size={15} color="#0F5132" /> Backup Recovery Codes
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCopyCodes}
                                            style={{
                                                background: copied ? '#ecfdf5' : '#ffffff',
                                                border: `1px solid ${copied ? '#a7f3d0' : '#cbd5e1'}`,
                                                color: copied ? '#059669' : '#334155',
                                                padding: '4px 10px',
                                                borderRadius: '8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy Codes</>}
                                        </button>
                                    </div>

                                    {/* Code Grid */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 700, color: '#334155', background: '#ffffff', padding: '0.75rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                        {recoveryCodes.map((code, idx) => (
                                            <div key={idx} style={{ padding: '4px 6px', background: '#f8fafc', borderRadius: '6px', textAlign: 'center' }}>
                                                {code}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleCompleteSetup}
                                    style={{
                                        width: '100%',
                                        height: '52px',
                                        background: '#0F5132',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '14px',
                                        fontSize: '1rem',
                                        fontWeight: 800,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        boxShadow: '0 10px 20px rgba(15, 81, 50, 0.25)',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <Sparkles size={18} /> Continue to Dashboard
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    /* TAB 2: UPDATE DEFAULT PASSWORD */
                    <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <div>
                            <h2 style={{ fontSize: '1.55rem', fontWeight: 900, color: '#0f172a', margin: '0 0 0.5rem 0', lineHeight: 1.25 }}>
                                Update Password
                            </h2>
                            <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0, lineHeight: 1.55 }}>
                                If you are logging in with a default or temporary password, please change it now to secure your account.
                            </p>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 800, color: '#334155' }}>Current / Default Password</label>
                            <input
                                type="password"
                                required
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Enter current password"
                                style={{
                                    height: '46px',
                                    padding: '0 12px',
                                    borderRadius: '10px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#f8fafc',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    outline: 'none'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 800, color: '#334155' }}>New Password (min. 8 characters)</label>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Create new strong password"
                                style={{
                                    height: '46px',
                                    padding: '0 12px',
                                    borderRadius: '10px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#f8fafc',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    outline: 'none'
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: 800, color: '#334155' }}>Confirm New Password</label>
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter new password"
                                style={{
                                    height: '46px',
                                    padding: '0 12px',
                                    borderRadius: '10px',
                                    border: '1.5px solid #e2e8f0',
                                    background: '#f8fafc',
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    outline: 'none'
                                }}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
                            style={{
                                width: '100%',
                                height: '52px',
                                background: '#0F5132',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '14px',
                                fontSize: '1rem',
                                fontWeight: 800,
                                cursor: isChangingPassword ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                boxShadow: '0 10px 20px rgba(15, 81, 50, 0.25)',
                                transition: 'all 0.2s',
                                marginTop: '0.5rem'
                            }}
                        >
                            {isChangingPassword ? <Loader2 size={20} className="animate-spin" /> : <>Save New Password <Key size={18} /></>}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};
